/**
 * Agentic Network room — Data Plane tab scoped to the pool pyramid:
 * Workspace → Network → pool → agents. Owners see ``@Pool`` + ``@AgentPool`` pickers only;
 * pool supervisors assigned to agents see ``@AgentPool`` for those agents only.
 *
 * Mirrors ``poolMembersApi.listPoolAgentSupervisors`` (effective supervisor email rows)
 * and reuses ``DataPlaneCatalog`` with ``agenticPoolScope``.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DataPlaneCatalog from '@stepflow/ui-shared/components/dataPlane/DataPlaneCatalog.jsx';
import { listPoolAgentSupervisors } from '@stepflow/ui-shared/services/poolMembersApi.js';

function normalizeEmail(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase();
}

/** True when viewer is listed as primary or co-supervisor on the agent row. */
function viewerMatchesSupervisors(row, emailNorm) {
  if (!emailNorm) return false;
  const list = Array.isArray(row?.effectiveSupervisorEmails)
    ? row.effectiveSupervisorEmails
    : [];
  const normalizedList = list
    .map((e) =>
      String(e ?? '')
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  if (normalizedList.length) {
    return normalizedList.includes(emailNorm);
  }
  return normalizeEmail(effectiveSupervisorEmail(row)) === emailNorm;
}

function effectiveSupervisorEmail(row) {
  const nested = row?.effectiveSupervisor && typeof row.effectiveSupervisor === 'object'
    ? row.effectiveSupervisor.email
    : null;
  return nested || row?.effectiveSupervisorEmail || '';
}

export default function AgenticDataPlaneTab({
  networkId,
  poolId,
  // leadPoolId — no longer the context anchor under §27 (context is per-room); accepted but unused.
  viewerEmail,
  poolRole,
  workspaceRole,
  lifecycleFrozen,
}) {
  const [matrix, setMatrix] = useState(null);
  const [matrixError, setMatrixError] = useState(null);

  const emailNorm = useMemo(() => normalizeEmail(viewerEmail), [viewerEmail]);

  useEffect(() => {
    if (!networkId?.trim() || !poolId?.trim()) return undefined;
    let cancelled = false;
    setMatrixError(null);
    listPoolAgentSupervisors(networkId, poolId)
      .then((data) => {
        if (cancelled) return;
        setMatrix(Array.isArray(data?.agents) ? data.agents : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setMatrix(null);
        setMatrixError(e?.message || 'Failed to load supervisor matrix');
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, poolId]);

  const poolOwnerTier = useMemo(() => {
    const wr = String(workspaceRole || '').toUpperCase().trim();
    if (wr === 'OWNER' || wr === 'ADMIN') return true;
    return String(poolRole || '').toUpperCase().trim() === 'CREATOR';
  }, [workspaceRole, poolRole]);

  const allAgentLogicalIds = useMemo(() => {
    if (!Array.isArray(matrix)) return [];
    return matrix
      .map((row) => String(row?.agentId ?? '').trim())
      .filter((id) => id.length > 0);
  }, [matrix]);

  const supervisedAgentLogicalIds = useMemo(() => {
    if (!Array.isArray(matrix) || !emailNorm) return [];
    return matrix
      .filter((row) => viewerMatchesSupervisors(row, emailNorm))
      .map((row) => String(row?.agentId ?? '').trim())
      .filter((id) => id.length > 0);
  }, [matrix, emailNorm]);

  /** Mount picker agents: owners use full ASL list; supervisors use their supervised subset. */
  const catalogAgentNames = useMemo(() => {
    return poolOwnerTier ? allAgentLogicalIds : supervisedAgentLogicalIds;
  }, [poolOwnerTier, allAgentLogicalIds, supervisedAgentLogicalIds]);

  // §27 PER-ROOM CONTEXT (reverses the old §13 shared-anchor): a room's context is its OWN — the
  // Context tab DISPLAYs/UPLOADs/DELETEs against the room's OWN pool. Other rooms' uploads appear as
  // OFF references in the "<room>-shared" section (derive-once + CTXTIER control). ``leadPoolId`` is
  // no longer the anchor. (Connections/workspace/ledger stay Lead-shared — see CONTEXT.md §27.)
  const ctxAnchorPool = useMemo(() => String(poolId || '').trim(), [poolId]);

  const agenticPoolScope = useMemo(() => {
    if (!ctxAnchorPool) return null;
    if (poolOwnerTier) return { poolId: ctxAnchorPool };
    return { poolId: ctxAnchorPool, supervisorAgentIds: supervisedAgentLogicalIds };
  }, [ctxAnchorPool, poolOwnerTier, supervisedAgentLogicalIds]);

  const workspaceViewer =
    String(workspaceRole || '').toUpperCase().trim() === 'VIEWER';
  const showUpload =
    !workspaceViewer && !lifecycleFrozen && !!(networkId?.trim() && poolId?.trim());

  const netTrim = typeof networkId === 'string' ? networkId.trim() : '';
  const poolTrim = typeof poolId === 'string' ? poolId.trim() : '';

  if (!netTrim || !poolTrim) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 p-8 text-xs text-gray-600">
        Context needs a deployed network id and pool id.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {matrixError ? (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-950">
          {matrixError}. Mount pickers fall back limited until refreshed.
        </div>
      ) : null}

      {matrix !== null && !poolOwnerTier && supervisedAgentLogicalIds.length === 0 ? (
        <div className="shrink-0 border-b border-sky-100 bg-sky-50 px-4 py-2 text-xs text-sky-950">
          You are signed in only as a pool supervisor — add per-agent supervisors in{' '}
          <span className="font-semibold">Members</span> before you can tag files with{' '}
          <span className="font-mono text-[11px]">@AgentPool</span> here.
        </div>
      ) : null}

      {/* §2.6a: the room group's planning-context digest — the distilled outcome of the
          uploaded/planning docs — is surfaced INSIDE the catalog below as a VIRTUAL room-wide
          RULES row ("Planning context index — Room wide") with an OFF/RULES toggle, mirroring the
          SKILL rows. Its OFF is enforced in the shared merge, so the per-agent "View context"
          preview honors it from the same source. (No separate top block — it lives in the list.) */}

      {/* §2.6b: skills-as-context now folds into the catalog as SKILL-lane rows
          (agents are the subscribers). Skill subscriptions are network-wide, so the
          chips offer the full agent roster; only pool owners may edit them here. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <DataPlaneCatalog
          networkId={netTrim}
          listFilter={{ networkId: netTrim }}
          agentNames={catalogAgentNames}
          poolIds={[ctxAnchorPool]}
          includeOrg={false}
          includeOrgInNetworkScopedList={false}
          showUpload={showUpload}
          agenticPoolScope={agenticPoolScope}
          skillsCanManage={poolOwnerTier}
        />
      </div>
    </div>
  );
}
